<script setup lang="ts">
import type { PropType } from "vue";
import type { CollectionType, VideoType } from "~/types";
import moment from "moment";
import useNotifications from "~/composables/useNotifications";
import useCollectionAiredData from "~/composables/useCollectionAiredData";
import { getFormattedTimestamp, isCollection } from "~/utils/common";

const props = defineProps({
  type: {
    type: String as PropType<CollectionType>,
    required: true,
  },
  videoType: {
    type: String as PropType<VideoType>,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  subheading: {
    type: String,
    default: "",
  },
  commentaryBy: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    required: true,
  },
  thumbnail: {
    type: String,
    default: "",
  },
  airDate: {
    type: String,
    required: true,
  },
  notificationSet: {
    type: Boolean,
    required: true,
  },
  enableNotifications: {
    type: Boolean,
    default: true,
  },
  dateFormat: {
    type: String,
    default: "MMM DD, hh:mm A",
  },
  isNewEpisode: {
    type: Boolean,
  },
  isLive: {
    type: Boolean,
  },
  isAiringToday: {
    type: Boolean,
  },
});

const {
  notificationsTooltip,
  notificationsIcon,
  toggleNotifications,
  isNotificationsLoading,
  notificationsVisible,
} = useNotifications({
  NotificationSet: props.notificationSet,
  type: props.type,
  title: props.title,
  aired_at: props.airDate,
  id: props.id,
});

const { chipInfo } = useCollectionAiredData({
  aired_at: props.airDate,
  is_new_episode: props.isNewEpisode,
  is_live: props.isLive,
  is_airing_today: props.isAiringToday,
});

async function handleBellClick() {
  await toggleNotifications();
}

const toLink = computed(() => {
  if (isCollection(props.type)) {
    return `/collections/${props.label}`;
  } else {
    return `/videos/${props.label}`;
  }
});
</script>

<template>
  <NuxtLink
    :to="toLink"
    :class="[
      'items-center rounded-lg border border-surface-border p-3 hover:bg-primary-900 lg:flex',
    ]"
  >
    <div class="flex flex-grow">
      <div class="aspect-video w-32 lg:w-44">
        <NuxtImg
          alt="thumbnail"
          class="aspect-video w-full"
          :src="thumbnail"
          placeholder="/images/placeholder.png"
          loading="lazy"
        />
      </div>
      <div class="flex w-min flex-grow flex-col justify-around gap-1 pl-4">
        <div v-if="chipInfo.label">
          <PgChip
            :icon="chipInfo.icon"
            :label="chipInfo.label"
            :bg="chipInfo.bg"
          />
        </div>

        <div class="line-clamp-2 text-xl">{{ title }}</div>

        <div v-if="description" class="my-2 line-clamp-2 text-sm">
          {{ description }}
        </div>

        <div v-if="subheading" class="text-sm text-primary-200">
          {{ subheading }}
        </div>

        <div class="hidden items-center gap-4 text-sm font-light lg:flex">
          <div v-if="airDate">
            <font-awesome-icon :icon="['fas', 'clock']" class="mr-2" />
            {{ getFormattedTimestamp(airDate, dateFormat) }}
          </div>

          <div v-if="commentaryBy">
            Commentary by:
            {{ commentaryBy }}
          </div>
        </div>
      </div>
    </div>
    <div class="mt-3 flex items-center justify-between lg:mt-0">
      <div class="flex gap-3 text-sm font-light lg:hidden">
        <div class="flex-shrink-0">
          <font-awesome-icon :icon="['fas', 'clock']" class="mr-2" />
          <span>{{ getFormattedTimestamp(airDate, dateFormat) }}</span>
        </div>
        <div v-if="commentaryBy">
          Commentary by:
          {{ commentaryBy }}
        </div>
      </div>

      <div
        v-if="enableNotifications"
        @click.stop.prevent
        @click="handleBellClick"
      >
        <PgIcon
          v-if="notificationsVisible"
          v-tooltip="notificationsTooltip"
          :icon="notificationsIcon"
          :loading="isNotificationsLoading"
        />
      </div>
    </div>
  </NuxtLink>
</template>

<style scoped></style>
